﻿import * as MUI from "@material-ui/core";
import * as React from "react";
import {
    FileDisplay,
    FileElement,
    FileUpload,
    TextField,
    ThematicBreak,
} from "../Components";
import { Dictionary } from "../../Utilities";
import { IFrameCommManager } from "../Managers";
import { IReturnModel } from "../IReturnModel";
import { SupportHelper } from "./";

export interface IContactFormProps {
    showErrorPageCallback: () => void;
}

interface IUploadDTO {
    browserDebugInfo: string;
    comments: string;
    description: string;
    expectations: string;
    mainUrl: string;
    selectedFrequency: string;
    title: string;
    viewShareLink: string;
}

export interface IContactFormState {
    areUploaded: Dictionary<string, boolean>;
    attachments: Dictionary<string, File>;
    comments: string;
    description: string;
    descriptionError: string;
    errorMessage: string;
    expectations: string;
    expectationsError: string;
    frequencyError: string;
    frequencyOptions: string[];
    mainUrl: string;
    progress: "input" | "loading" | "confirmation";
    selectedFrequency: string;
    title: string;
    titleError: string;
}

interface IInteralContactFormProps {
    areUploaded: Dictionary<string, boolean>;
    attachments: Dictionary<string, File>;
    comments: string;
    description: string;
    descriptionError: string;
    errorMessage: string;
    expectations: string;
    expectationsError: string;
    frequencyError: string;
    frequencyOptions: string[];
    handleCommentsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleExpectationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleFrequencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    loadFile: (file: File) => void;
    mainUrl: string;
    onUploadComplete: (fileName: string) => void;
    postRequest: () => Promise<void>;
    selectedFrequency: string;
    title: string;
    titleError: string;
    unloadFile: (file: File) => void;
}

export class ContactForm extends React.Component<
    IContactFormProps,
    IContactFormState
> {
    constructor(props: IContactFormProps) {
        super(props);

        this.state = {
            areUploaded: new Dictionary<string, boolean>(),
            attachments: new Dictionary<string, File>(),
            comments: ``,
            description: ``,
            descriptionError: null,
            errorMessage: null,
            expectations: ``,
            expectationsError: null,
            frequencyError: null,
            frequencyOptions: [],
            mainUrl: MAIN_URL,
            progress: `input`,
            selectedFrequency: ``,
            title: ``,
            titleError: null,
        };
        // Setup the channel for the viewshare messages.
        IFrameCommManager.registerOrUpdateWatcher(
            this._key,
            this._onMessageReceived,
        );
        this._requestLink();
    }

    componentDidMount(): void {
        void this._getInitialState();
    }

    componentWillUnmount(): void {
        IFrameCommManager.deRegisterWatcher(this._key);
    }

    private _messageListeningState: `Listening` | `NotListening`;

    private readonly _key = `SmartSupportWindow`;

    private _recievedID: string;

    private _recievedLink: string;

    private _toolModuleId: number;

    private readonly _requestLink = (): void => {
        this._messageListeningState = `Listening`;
        IFrameCommManager.messageTool(`Link?`);
        setTimeout(this._onNoLink, 3000);
    };

    private readonly _linkReceived = (
        moduleId: number,
        message: string,
    ): void => {
        this._messageListeningState = `NotListening`;
        this._recievedLink = message;
        this._toolModuleId = moduleId;
        void this._getViewShareLink();
    };

    private readonly _onNoLink = (): void => {
        if (this._messageListeningState === `Listening`) {
            this._messageListeningState = `NotListening`;
            this._recievedID = `ViewShare link could not be retrieved.`;
        }
    };

    private readonly _onMessageReceived = (
        moduleId: number,
        message: string,
    ): void => {
        if (this._messageListeningState === `Listening`) {
            if (message) {
                this._linkReceived(moduleId, message);
            }
        }
    };

    private readonly _getViewShareLink = async (): Promise<void> => {
        const resultText: string = await $.ajax({
            data: {
                extension: this._recievedLink,
                moduleId: this._toolModuleId,
                viewShareName: ``,
                keyWords: ``,
            },
            method: `POST`,
            url: `/ViewShare/NewViewShare`,
        });

        const resultObj: { viewShareId: string } = JSON.parse(resultText);

        const recievedID: string = resultObj.viewShareId;

        this._recievedID = `${window.location.origin}/ViewShare-${recievedID}`;
    };

    private readonly _getInitialState = async (): Promise<void> => {
        const stringResult: string = await $.ajax(`/Support/Contact`, {
            method: `GET`,
        });

        const result: IReturnModel<IContactFormState> =
            JSON.parse(stringResult);

        this.setState({
            frequencyOptions: result.value.frequencyOptions,
        });
    };

    private readonly _handleTitleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        this.setState({
            title: e.target.value,
        });
    };

    private readonly _handleDescriptionChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        this.setState({
            description: e.target.value,
        });
    };

    private readonly _handleFrequencyChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        this.setState({
            selectedFrequency: e.target.value,
        });
    };

    private readonly _handleExpectationChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        this.setState({
            expectations: e.target.value,
        });
    };

    private readonly _handleCommentsChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        this.setState({
            comments: e.target.value,
        });
    };

    private readonly _loadFile = (file: File): void => {
        this.setState((prev) => {
            const files: Dictionary<string, File> = prev.attachments,
                uploadedStatuses: Dictionary<string, boolean> =
                    prev.areUploaded;

            files.push(file.name, file);
            uploadedStatuses.push(file.name, false);

            return {
                areUploaded: uploadedStatuses,
                attachments: files,
            };
        });
    };

    private readonly _unloadFile = (file: File): void => {
        this.setState((prev) => {
            const files: Dictionary<string, File> = prev.attachments,
                uploadedStatuses: Dictionary<string, boolean> =
                    prev.areUploaded;

            files.remove(file.name);
            uploadedStatuses.remove(file.name);

            return {
                areUploaded: uploadedStatuses,
                attachments: files,
            };
        });
    };

    private readonly _onUploadComplete = (fileName: string): void => {
        this.setState((prev) => {
            const uploadedStatuses: Dictionary<string, boolean> =
                prev.areUploaded;

            uploadedStatuses.set(fileName, true);

            return {
                areUploaded: uploadedStatuses,
            };
        });
    };

    private readonly _postRequest = async (): Promise<void> => {
        const descriptionError: boolean = this.state.description === ``,
            expectationsError: boolean = this.state.expectations === ``,
            frequencyError: boolean = this.state.selectedFrequency === ``,
            titleError: boolean = this.state.title === ``;

        this.setState({
            descriptionError: descriptionError
                ? `Please fill in Problem Description`
                : null,
            expectationsError: expectationsError
                ? `Please fill in your expectations of how the tool should behave.`
                : null,
            frequencyError: frequencyError
                ? `Please select an option for "How frequent is the problem?"`
                : null,
            titleError: titleError ? `Please fill in Problem Summary` : null,
        });

        if (
            titleError ||
            descriptionError ||
            frequencyError ||
            expectationsError
        ) {
            return;
        }

        const postData: IUploadDTO = {
            browserDebugInfo: SupportHelper.getBrowserInfo(),
            comments: this.state.comments,
            description: this.state.description,
            expectations: this.state.expectations,
            mainUrl: this.state.mainUrl,
            selectedFrequency: this.state.selectedFrequency,
            title: this.state.title,
            viewShareLink: this._recievedID,
        };

        this.setState({
            progress: `loading`,
        });

        const resultString: string = await $.ajax(`/Support/SubmitContact`, {
            data: postData,
            method: `POST`,
        });

        const result: IReturnModel<null> = JSON.parse(resultString);

        if (result.isSuccess) {
            this.setState({
                progress: `confirmation`,
            });
        } else if (result.errorMessage !== `` && result.errorMessage !== null) {
            this.setState({
                errorMessage: result.errorMessage,
                progress: `input`,
            });
        } else {
            this.props.showErrorPageCallback();
        }
    };

    private readonly _contactForm = (
        props: IInteralContactFormProps,
    ): JSX.Element => {
        let isSubitEnabled = true;

        const isUploaded: (key: string, value: boolean) => void = (
            key: string,
            value: boolean,
        ): void => {
            if (!value) {
                isSubitEnabled = value;
            }
        };

        props.areUploaded.forEach(isUploaded);

        return (
            <React.Fragment>
                <p>
                    {`Having a problem? Fill out the form below to contact our support agents. 
                        Please have the problem loaded on your screen, as we send this information to our agents.`}
                    <br />
                    <br />
                    All fields marked with * are mandatory.
                </p>
                <h3>Problem</h3>
                {props.errorMessage && props.errorMessage}
                <TextField
                    characterlimit={40}
                    className={`smartsupport-textinput`}
                    error={props.titleError !== null}
                    label={props.titleError || `Problem Summary`}
                    onChange={props.handleTitleChange}
                    required
                    value={props.title}
                />
                <TextField
                    characterlimit={300}
                    className={`smartsupport-textinput`}
                    error={props.descriptionError !== null}
                    inputProps={{ style: { overflow: `auto` } }}
                    label={props.descriptionError || `Problem Description`}
                    multiline
                    onChange={props.handleDescriptionChange}
                    required
                    rows={`5`}
                    value={props.description}
                />
                <MUI.FormControl
                    className={`smartsupport-formcontrol`}
                    error={props.frequencyError !== null}
                >
                    <MUI.InputLabel id={`frequency-label`}>
                        {props.frequencyError ||
                            `How frequent is the problem?*`}
                    </MUI.InputLabel>
                    <MUI.Select
                        className={`smartsupport-select`}
                        labelId={`frequency-label`}
                        onChange={props.handleFrequencyChange}
                        value={props.selectedFrequency}
                    >
                        {props.frequencyOptions.map(
                            (o): JSX.Element => (
                                <MUI.MenuItem key={o} value={o}>
                                    {o}
                                </MUI.MenuItem>
                            ),
                        )}
                    </MUI.Select>
                </MUI.FormControl>
                <TextField
                    characterlimit={60}
                    className={`smartsupport-textinput`}
                    error={props.expectationsError !== null}
                    label={props.expectationsError || `Expectations`}
                    onChange={props.handleExpectationChange}
                    required
                    value={props.expectations}
                />
                <TextField
                    characterlimit={200}
                    className={`smartsupport-textinput`}
                    inputProps={{
                        style: {
                            overflow: `auto`,
                        },
                    }}
                    label={`Additional Comments`}
                    multiline
                    onChange={props.handleCommentsChange}
                    rows={`3`}
                    value={props.comments}
                />
                <div className={`filecontainer`}>
                    {props.attachments.map(
                        (n, f): JSX.Element => (
                            <FileElement
                                file={f}
                                key={n}
                                onCompleteCallback={props.onUploadComplete}
                                postURL={`/Support/Attachment`}
                                removeFileCallback={props.unloadFile}
                            />
                        ),
                    )}
                </div>
                <FileUpload fileLoadCallback={props.loadFile} />
                <MUI.Button
                    className={`smartsupport-submit`}
                    color={`primary`}
                    disabled={!isSubitEnabled}
                    onClick={props.postRequest}
                >
                    Submit
                </MUI.Button>
            </React.Fragment>
        );
    };

    private _awaitingResponce(): JSX.Element {
        return (
            <div className={`support-loadingcontainer`}>
                <img
                    className={`support-loading`}
                    src={`/Images/LoadingAnimation.svg`}
                />
            </div>
        );
    }

    private _contactSubmitted(props: IContactFormState): JSX.Element {
        return (
            <div className={`smartsuppport-submitoutput`}>
                <img src={`/Images/SupportConfirmation.svg`} />
                <p>
                    {`Thank you for sending your ticket to Metricell support. 
                        An email will be sent to your email automatically to confirm your ticket creation. 
                        We hope to resolve your problem soon. Please see a summary of your ticket below:`}
                </p>
                <ThematicBreak />
                <h2>Problem</h2>
                <div className={`smartsupport-summary`}>
                    <MUI.InputLabel id={`Title-label`}>Title</MUI.InputLabel>
                    <div>{props.title}</div>
                    <MUI.InputLabel id={`Description-label`}>
                        Problem Description
                    </MUI.InputLabel>
                    <div>{props.description}</div>
                    <MUI.InputLabel id={`SelectedFrequency-label`}>
                        How frequent is the problem?
                    </MUI.InputLabel>
                    <div>{props.selectedFrequency}</div>
                    <MUI.InputLabel id={`Expectations-label`}>
                        Expectations?
                    </MUI.InputLabel>
                    <div>{props.expectations}</div>
                    <MUI.InputLabel id={`Comments-label`}>
                        Additional Comments
                    </MUI.InputLabel>
                    <div>{props.comments}</div>
                </div>
                <div className={`filecontainer`}>
                    {props.attachments.map(
                        (n): JSX.Element => (
                            <FileDisplay fileName={n} key={n} />
                        ),
                    )}
                </div>
            </div>
        );
    }

    render(): React.ReactElement {
        return (
            <React.Fragment>
                {this.state.progress === `input` && (
                    <this._contactForm
                        handleCommentsChange={this._handleCommentsChange}
                        handleDescriptionChange={this._handleDescriptionChange}
                        handleExpectationChange={this._handleExpectationChange}
                        handleFrequencyChange={this._handleFrequencyChange}
                        handleTitleChange={this._handleTitleChange}
                        loadFile={this._loadFile}
                        onUploadComplete={this._onUploadComplete}
                        postRequest={this._postRequest}
                        unloadFile={this._unloadFile}
                        {...this.state}
                    />
                )}
                {this.state.progress === `loading` && (
                    <this._awaitingResponce />
                )}
                {this.state.progress === `confirmation` && (
                    <this._contactSubmitted {...this.state} />
                )}
            </React.Fragment>
        );
    }
}
